import { Order } from '@/types';
import { Box, Paper } from '@mui/material';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';

const hourlabels = ["6am", "7am", "8am", "9am", "10am", "11am", "12pm", "1pm", "2pm", "3pm", "4pm", "5pm", "6pm", "7pm", "8pm"];
const dayLabels = ["", "L", "M", "M", "J", "V", "S", "D"];

interface Props {
    data?: Order[];
}

const TimingHeadmap = ({ data = [] }: Props) => {
    const [weeklyActiveOrders, min, max, startIndex, endIndex] = useMemo(() => {
        // Create a 7x15 matrix (7 days x 15 hour slots: 6am - 8pm)
        const matrix = Array.from({ length: 7 }, () => Array(15).fill(0));
        let min = 0;
        let max = 0;

        data.forEach((order) => {
            const startTimestamp = order.orderedAt || order.createdAt;
            const endTimestamp = order.completedAt || startTimestamp;

            if (!startTimestamp) return;

            const start = new Date(startTimestamp);
            const end = new Date(endTimestamp);

            const day = (start.getDay() + 6) % 7; // 0 (Mon) to 6 (Sun)

            // Hours: 6 (6 am) to 20 (8 pm)
            for (let h = 6; h <= 20; h++) {
                // If the order was active during hour slot 'h'
                if (start.getHours() <= h && end.getHours() >= h) {
                    matrix[day][h - 6]++;
                    if (matrix[day][h - 6] > max) {
                        max = matrix[day][h - 6];
                    }
                    if (matrix[day][h - 6] < min) {
                        min = matrix[day][h - 6];
                    }
                }
            }
        });

        let startIndex = 0;
        let endIndex = 14;
        let foundStart = false;

        // Find startIndex (first hour with data)
        for (let h = 0; h < 15; h++) {
            for (let d = 0; d < 7; d++) {
                if (matrix[d][h] > 0) {
                    startIndex = h;
                    foundStart = true;
                    break;
                }
            }
            if (foundStart) break;
        }

        // Find endIndex (last hour with data)
        let foundEnd = false;
        for (let h = 14; h >= 0; h--) {
            for (let d = 0; d < 7; d++) {
                if (matrix[d][h] > 0) {
                    endIndex = h;
                    foundEnd = true;
                    break;
                }
            }
            if (foundEnd) break;
        }

        // If no data at all, hide everything or show a default range? 
        if (!foundStart) {
            startIndex = 15; // Greater than 14 to hide all
            endIndex = -1;  // Less than 0 to hide all
        }

        return [matrix, min, max, startIndex, endIndex];
    }, [data]);

    const visibleHoursCount = Math.max(0, (endIndex as number) - (startIndex as number) + 1);

    return (
        <Paper sx={{
            padding: 2
        }}>
            <Stack spacing={2}>
                <Typography variant="overline" color="textSecondary">Órdenes Activas por Horario</Typography>
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(8, 1fr)",
                        gridTemplateRows: `auto repeat(${visibleHoursCount}, 1fr)`,
                        gridRowGap: 4,
                        gridColumnGap: 4,
                        ['& .cell']: {
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: 40,
                        },
                        ['& .hidden']: {
                            display: 'none',
                        }
                    }}
                >
                    {dayLabels.map((dayLabel, dayLabelIndex) => {
                        return (
                            <Box key={`dayLabel-${dayLabelIndex}`} className="cell">
                                <Typography variant="overline">{dayLabel}</Typography>
                            </Box>
                        )
                    })}
                    {hourlabels.map((hourLabel, hourIndex) => {
                        const isHidden = hourIndex < (startIndex as number) || hourIndex > (endIndex as number);
                        const className = `cell ${isHidden ? 'hidden' : ''}`;
                        return (
                            <Box key={`hourRow-${hourIndex}`} component="div" sx={{ display: 'contents' }}>
                                <Box className={className}>
                                    <Typography variant="overline">{hourLabel}</Typography>
                                </Box>
                                {weeklyActiveOrders.map((dayData, dayIndex) => {
                                    const value = dayData[hourIndex];
                                    return (
                                        <Box
                                            key={`hourdata-${hourIndex}-${dayIndex}`}
                                            className={className}
                                            sx={{
                                                borderRadius: 1,
                                                background: value > 0 ? `rgba(255, 145, 0, ${max === min ? 0 : (value - min) / (max - min)})` : '#F8F8F8',
                                            }}>
                                            {value > 0 && <Typography color="text.primary">{value}</Typography>}
                                        </Box>
                                    )
                                })}
                            </Box>
                        )
                    })}
                </Box>
            </Stack>
        </Paper>
    );
}

export default TimingHeadmap;
